import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { quizAPI } from '../services/api';
import { FiArrowLeft, FiFolder, FiFileText, FiVideo, FiEdit, FiRefreshCw, FiCheckCircle, FiXCircle } from 'react-icons/fi';

const CourseContents = () => {
  const { courseId, folderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { course } = location.state || {};

  const [folders, setFolders] = useState([]);
  const [videos, setVideos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [testSeries, setTestSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseTitle, setCourseTitle] = useState(course?.title || 'Course');

  useEffect(() => {
    if (folderId) {
      // Fetch contents of a specific folder
      fetchFolderContents();
    } else {
      // Fetch parent folders of the course
      fetchParentFolders();
    }
  }, [courseId, folderId]);

  const fetchParentFolders = async () => {
    try {
      const response = await quizAPI.getParentFolders(courseId);
      if (response.data.success) {
        setFolders(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolderContents = async () => {
    try {
      // Get subfolders
      const foldersResponse = await quizAPI.getSubFolders(courseId, folderId);
      if (foldersResponse.data.success) {
        setFolders(foldersResponse.data.data || []);
      }

      // Get contents (videos, documents, test series)
      const contentsResponse = await quizAPI.getFolderContents(courseId, folderId);
      if (contentsResponse.data.success) {
        setVideos(contentsResponse.data.videos || []);
        setDocuments(contentsResponse.data.documents || []);
        setTestSeries(contentsResponse.data.testseries || []);
      }
    } catch (error) {
      console.error('Error fetching contents:', error);
    } finally {
      setLoading(false);
    }
  };

  const openFolder = (folder) => {
    navigate(`/course/${courseId}/folder/${folder.folder_id}`, {
      state: { course, folderName: folder.folders }
    });
  };

  const startTest = (test) => {
    navigate(`/test-series/${test.exam_id}`, {
      state: { test, courseId, folderId,course }
    });
  };

  const playVideo = (video) => {
    navigate('/content-viewer', {
      state: {
        content: video.video_id,
        type: 'video',
        title: video.title,
        description: video.description
      }
    });
  };

  const viewDocument = (doc) => {
    navigate('/content-viewer', {
      state: {
        content: doc.document,
        type: 'pdf',
        title: doc.doc_name,
        description: doc.description
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100"
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-800">{courseTitle}</h1>
              {location.state?.folderName && (
                <p className="text-sm text-gray-600">{location.state.folderName}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary-500 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading contents...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Folders Section */}
            {folders.length > 0 && (
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mr-3">
                    <FiFolder className="w-5 h-5 text-yellow-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">Folders</h2>
                  <span className="ml-auto bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {folders.length}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {folders.map((folder) => (
                    <div
                      key={folder.id}
                      onClick={() => openFolder(folder)}
                      className="card hover:shadow-xl transition-all cursor-pointer group"
                    >
                      <div className="flex items-center">
                        <div className="w-16 h-16 rounded-lg bg-yellow-100 flex items-center justify-center mr-4">
                          <FiFolder className="w-8 h-8 text-yellow-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800 group-hover:text-primary-500 transition-colors">
                            {folder.folders}
                          </h3>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            {folder.videos > 0 && (
                              <span className="flex items-center">
                                <FiVideo className="mr-1" /> {folder.videos} Videos
                              </span>
                            )}
                            {folder.documents > 0 && (
                              <span className="flex items-center">
                                <FiFileText className="mr-1" /> {folder.documents} Docs
                              </span>
                            )}
                            {folder.testseries > 0 && (
                              <span className="flex items-center">
                                <FiEdit className="mr-1" /> {folder.testseries} Tests
                              </span>
                            )}
                          </div>
                        </div>
                        <FiArrowLeft className="w-5 h-5 text-gray-400 transform rotate-180" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Videos Section */}
            {videos.length > 0 && (
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                    <FiVideo className="w-5 h-5 text-red-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">Videos</h2>
                  <span className="ml-auto bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {videos.length}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {videos.map((video) => (
                    <div
                      key={video.id}
                      className="card hover:shadow-xl transition-all group"
                    >
                      <div className="flex flex-col">
                        {/* Video Thumbnail */}
                        <div className="relative w-full pb-[56.25%] bg-gray-100 rounded-lg overflow-hidden mb-3">
                          <img
                            src={`http://img.youtube.com/vi/${video.video_id}/hqdefault.jpg`}
                            alt={video.title}
                            className="absolute top-0 left-0 w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                              <FiVideo className="w-8 h-8 text-white ml-1" />
                            </div>
                          </div>
                        </div>

                        {/* Video Info */}
                        <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2">{video.title}</h3>
                        {video.description && (
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                            {video.description.replace(/(&nbsp|amp|quot|lt|gt|;|<([^>]+)>)/gi, '')}
                          </p>
                        )}

                        <button
                          onClick={() => playVideo(video)}
                          className="btn-primary w-full"
                        >
                          <FiVideo className="mr-2" />
                          Watch Video
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents Section */}
            {documents.length > 0 && (
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3">
                    <FiFileText className="w-5 h-5 text-orange-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">Documents</h2>
                  <span className="ml-auto bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {documents.length}
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="card hover:shadow-xl transition-all"
                    >
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="w-full md:w-20 h-24 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <FiFileText className="w-10 h-10 text-orange-600" />
                        </div>

                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-800 mb-2">{doc.doc_name}</h3>
                          {doc.description && (
                            <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                              {doc.description.replace(/(&nbsp|amp|quot|lt|gt|;|<([^>]+)>)/gi, '')}
                            </p>
                          )}
                        </div>

                        <div className="flex md:flex-col gap-2">
                          <button
                            onClick={() => viewDocument(doc)}
                            className="btn-primary flex-1 md:flex-none whitespace-nowrap px-6"
                          >
                            <FiFileText className="mr-2" />
                            View PDF
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Test Series Section */}
            {testSeries.length > 0 && (
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                    <FiEdit className="w-5 h-5 text-purple-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">Test Series</h2>
                  <span className="ml-auto bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {testSeries.length}
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {testSeries.map((test) => (
                    <div
                      key={test.id}
                      className="card hover:shadow-xl transition-all"
                    >
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="w-20 h-20 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <FiFileText className="w-10 h-10 text-blue-600" />
                        </div>

                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-800 mb-2">{test.title}</h3>

                          <div className="flex flex-wrap gap-4 text-sm">
                            <span className="flex items-center text-gray-600">
                              <FiRefreshCw className="w-4 h-4 mr-1" />
                              Attempts: {test.attempt}
                            </span>
                            <span className="flex items-center text-green-600">
                              <FiCheckCircle className="w-4 h-4 mr-1" />
                              Marks: {test.marks}
                            </span>
                            <span className="flex items-center text-red-600">
                              <FiXCircle className="w-4 h-4 mr-1" />
                              Negative: {test.negative_marks}
                            </span>
                          </div>
                        </div>
                        
                        {test.is_free ? (
                        
                            <button
                              onClick={() => startTest(test)}
                            
                              className="btn-primary px-8 whitespace-nowrap"
                            >
                              Free  Test
                            </button>


                        ) : (
                          <>

                            {course.purchased ? (
                              <>
                              {test?.total_questions>0?  <button
                                onClick={() => startTest(test)}
                              
                                className="btn-primary px-8 whitespace-nowrap"
                              >
                                Start Test
                              </button>:  <button
                            disabled
                            className="px-8 py-2 bg-gray-300 text-gray-600 rounded-lg cursor-not-allowed whitespace-nowrap"
                          >
                            Coming Soon
                          </button>}
                            
                              </>
                            ) : (
                              <Link
                                to={`/my-course-details/${courseId}`}
                              
                                className="btn-secondary px-8 whitespace-nowrap"
                              >
                                Buy Course
                              </Link>
                            )}
                          </>
                        )}

                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {folders.length === 0 && testSeries.length === 0 && videos.length === 0 && documents.length === 0 && (
              <div className="card text-center py-12">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiFolder className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">No Content Available</h3>
                <p className="text-gray-600 mb-6">This section doesn't have any content yet</p>
                <Link to="/my-courses" className="btn-secondary inline-block">
                  Back to Courses
                </Link>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default CourseContents;

